import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  ChevronLeft,
  Clock,
  AlertCircle,
  Calendar,
  AlertTriangle,
  Flag,
  CheckCircle,
  User,
  Briefcase,
  List,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useNewTask } from '../hooks';
import { useActiveActivity } from '@/features/activities/hooks/useActivities';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getTaskStatusColor, getTaskPriorityVariant } from '../statusStyles';
import { AcceptTaskDialog } from '../components/AcceptTaskDialog';
import { AdjustmentDialog } from '../components/AdjustmentDialog';
import { AdjustmentsListModal } from '../components/AdjustmentsListModal';
import { canCreateAdjustment } from '../utils';
import type { TaskPriority } from '../types';

const getPriorityIcon = (priority: TaskPriority | undefined) => {
  switch (priority) {
    case 'HIGH':
      return <AlertTriangle className="h-4 w-4" />;
    case 'NORMAL':
      return <Flag className="h-4 w-4" />;
    case 'LOW':
      return <CheckCircle className="h-4 w-4" />;
    default:
      return <Flag className="h-4 w-4" />;
  }
};

export function NewTaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: task, isLoading, isError, error } = useNewTask(id);
  const { data: activeActivity } = useActiveActivity();
  const { hasAccess, user } = useAuth();

  const isMdPartner = user?.role?.type === 'MD';
  const roleType = user?.role?.type;

  if (isLoading) {
    return (
      <div className="container mx-auto space-y-6 p-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 text-center text-destructive">
        <p className="flex items-center justify-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {t('error')}: {error?.message}
        </p>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <FileText className="h-5 w-5 mx-auto mb-2" />
        {t('noDataAvailable')}
      </div>
    );
  }

  // MD Accept gate (mirrors the board's TaskActionsCell): the backend rejects
  // accepting a task with an unfinished adjustment, a running timer, or no
  // logged activity time — so disable the button up front in those cases.
  const hasActivities =
    (task.activities?.filter((a) => a.status !== 'REJECT')?.length ?? 0) > 0;
  const acceptDisabledReason = task.viewerHasUnfinishedAdjustment
    ? t('adjustment.finishFirst')
    : activeActivity?.taskId === task.id
      ? t('stopActivityBeforeReview')
      : !hasActivities
        ? t('noActivitiesToAccept')
        : undefined;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-6 w-6" />
            {t('taskDetails')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('taskId')}: {task.id}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Adjustments list — visible on originals that have children */}
          {!task.isAdjustment && (task.adjustmentsCount ?? 0) > 0 && (
            <AdjustmentsListModal
              taskId={task.id}
              adjustmentsCount={task.adjustmentsCount}
              currentUserId={user?.id}
            />
          )}

          {/* Create Adjustment — gated by matter-membership role.
              Hidden on terminal statuses (DONE/BILLED) — task is closed. */}
          {task.newStatus !== 'DONE' &&
            task.newStatus !== 'BILLED' &&
            canCreateAdjustment(roleType, task.newStatus) && (
              <AdjustmentDialog task={task} />
            )}

          {/* Accept Task — MD direct accept at BILLING_PENDING */}
          {isMdPartner && task.newStatus === 'BILLING_PENDING' && (
            <AcceptTaskDialog task={task} disabledReason={acceptDisabledReason} />
          )}

          <Button
            variant="outline"
            onClick={() => navigate('/app/new-tasks')}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            {t('backToList')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="bg-muted/50">
          <CardTitle className="flex items-center gap-2">
            <List className="h-5 w-5" />
            {t('taskInformation')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('title')}</p>
              <p className="font-medium text-lg">{task.title ?? '-'}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('description')}</p>
              <p className="whitespace-pre-line bg-muted/50 p-3 rounded-lg">
                {task.description || '-'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{t('status.status')}</p>
                <Badge className={`${getTaskStatusColor(task.newStatus)}`}>
                  {t(`newTaskStatus.${task.newStatus}`)}
                </Badge>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{t('priority')}</p>
                <Badge variant={getTaskPriorityVariant(task.priority)} className="gap-1">
                  {getPriorityIcon(task.priority)}
                  {task.priority ? t(`priority.${task.priority.toLowerCase()}`) : '-'}
                </Badge>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {t('dueDate')}
              </p>
              <p className="font-medium">
                {task.dueAt ? format(new Date(task.dueAt), 'PPpp') : '-'}
              </p>
            </div>
          </div>

          <Separator className="md:col-span-2" />

          <div className="space-y-4 md:col-span-2">
            <h3 className="font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('references')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{t('taskType')}</p>
                <p>{task.type?.name ?? '-'}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  {t('matter')}
                </p>
                {task.matter ? (
                  <button
                    type="button"
                    className="text-primary hover:underline text-left"
                    onClick={() => navigate(`/app/matters/${task.matter?.id}`)}
                  >
                    {task.matter.name || task.matter.caseNumber || '-'}
                  </button>
                ) : (
                  <p>-</p>
                )}
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t('assignedTo')}
                </p>
                <p>{task.assignedStaff?.name ?? '-'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="bg-muted/50">
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t('systemInformation')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
            <p className="font-medium">
              {task.createdAt ? format(new Date(task.createdAt), 'PPpp') : '-'}
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
            <p className="font-medium">
              {task.updatedAt ? format(new Date(task.updatedAt), 'PPpp') : '-'}
            </p>
          </div>
          {task.createdBy && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('createdBy')}</p>
              <p className="font-medium">{task.createdBy.name}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
